import { Box, Typography, Grid, Paper, Container, Stack } from '@mui/material';
import { motion, useInView } from 'framer-motion';
import { 
  Business, 
  Public, 
  WorkspacePremium, 
  EmojiEvents, 
  TrendingUp, 
  Language, 
  Speed,
  Timeline,
  Lightbulb,
  People
} from '@mui/icons-material';
import { useRef } from 'react';

const ExecutiveBio = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const achievements = [
    { icon: <Business />, label: 'Global Enterprises', value: '15+', description: 'Fortune 500 & multinational clients' },
    { icon: <Public />, label: 'Countries Operated', value: '40+', description: 'Across 6 continents' },
    { icon: <WorkspacePremium />, label: 'Years Experience', value: '25+', description: 'C-suite leadership roles' },
    { icon: <EmojiEvents />, label: 'Industry Awards', value: '12', description: 'For innovation & excellence' },
  ];

  const expertise = [
    { icon: <TrendingUp />, title: 'Strategic Growth', desc: 'Market expansion and scaling operations across emerging and established markets', color: '#C8A96B' },
    { icon: <Language />, title: 'Cross-Cultural Leadership', desc: 'Managing diverse international teams in 15+ languages and cultural contexts', color: '#C8A96B' },
    { icon: <Speed />, title: 'Operational Excellence', desc: 'Streamlining global processes and implementing best-in-class systems', color: '#C8A96B' },
    { icon: <Timeline />, title: 'Digital Transformation', desc: 'Leading technology adoption and innovation initiatives worldwide', color: '#C8A96B' },
    { icon: <Lightbulb />, title: 'Strategic Vision', desc: 'Identifying market opportunities and future-proofing business models', color: '#C8A96B' },
    { icon: <People />, title: 'Talent Development', desc: 'Building high-performance leadership teams across borders', color: '#C8A96B' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.12, duration: 0.6 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.4 } },
    hover: { scale: 1.03, y: -5, transition: { duration: 0.2 } },
  };

  const expertiseCardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
    hover: { y: -5, transition: { duration: 0.2 } },
  };

  return (
    <Box
      ref={ref}
      sx={{
        py: { xs: 10, md: 18 },
        background: 'linear-gradient(135deg, #0a0a0a 0%, #0f0f0f 50%, #0a0a0a 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '800px',
          height: '800px',
          background: 'radial-gradient(circle, rgba(200,169,107,0.05) 0%, transparent 70%)',
          borderRadius: '50%',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={containerVariants}
        >
          {/* Header Section - Centered */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="subtitle1"
                sx={{
                  color: '#C8A96B',
                  letterSpacing: 6,
                  mb: 2,
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                }}
              >
                Executive Leadership
              </Typography>

              <Typography
                variant="h2"
                sx={{
                  mb: 3,
                  fontSize: { xs: '2.2rem', sm: '3rem', md: '4rem' },
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #fff 0%, #C8A96B 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.02em',
                }}
              >
                John Asfar
              </Typography>

              <Typography
                sx={{
                  maxWidth: '800px',
                  mx: 'auto',
                  color: '#d0d0d0',
                  fontSize: { xs: '1rem', md: '1.2rem' },
                  lineHeight: 1.8,
                  mb: 4,
                }}
              >
                A visionary executive leader with extensive experience in global business
                operations, strategic infrastructure development, and innovative communication
                solutions across international markets. Proven track record of driving 
                sustainable growth and operational excellence in complex multinational environments.
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 8, flexWrap: 'wrap' }}>
                {['Visionary Leadership', 'Strategic Innovation', 'Global Mindset', 'Results-Driven'].map((tag, i) => (
                  <Paper
                    key={i}
                    sx={{
                      px: 2.5,
                      py: 0.75,
                      background: 'rgba(200,169,107,0.1)',
                      border: '1px solid rgba(200,169,107,0.3)',
                      borderRadius: '30px',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        background: 'rgba(200,169,107,0.2)',
                        borderColor: '#C8A96B',
                      },
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#C8A96B', fontWeight: 500 }}>
                      {tag}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Box>
          </motion.div>

          {/* Achievement Stats - Centered Cards */}
          <Grid container spacing={3} sx={{ mb: 12, justifyContent: 'center' }}>
            {achievements.map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={index} sx={{ display: 'flex', justifyContent: 'center' }}>
                <motion.div
                  variants={cardVariants}
                  initial="hidden"
                  animate={isInView ? "visible" : "hidden"}
                  whileHover="hover"
                  transition={{ delay: index * 0.1 }}
                  style={{ width: '100%' }}
                >
                  <Paper
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      background: 'linear-gradient(135deg, #111 0%, #161616 100%)',
                      border: '1px solid rgba(200,169,107,0.2)',
                      borderRadius: '20px',
                      transition: 'all 0.3s',
                      cursor: 'pointer',
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '200px',
                      '&:hover': {
                        borderColor: '#C8A96B',
                        boxShadow: '0 10px 30px rgba(200,169,107,0.1)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        color: '#C8A96B',
                        fontSize: 42,
                        mb: 1.5,
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 800,
                        mb: 0.5,
                        background: 'linear-gradient(135deg, #fff 0%, #C8A96B 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontSize: { xs: '2rem', md: '2.5rem' },
                      }}
                    >
                      {item.value}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600, mb: 0.5 }}>
                      {item.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#888' }}>
                      {item.description}
                    </Typography>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* Expertise Section - Centered Header */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="h4"
                sx={{
                  color: '#C8A96B',
                  fontWeight: 700,
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 80,
                    height: 3,
                    background: '#C8A96B',
                    borderRadius: '2px',
                  },
                }}
              >
                Core Areas of Expertise
              </Typography>
            </Box>
          </motion.div>

          {/* Expertise Cards - Centered Grid */}
          <Grid container spacing={3} sx={{ mb: 10, justifyContent: 'center' }}>
            {expertise.map((exp, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx} sx={{ display: 'flex', justifyContent: 'center' }}>
                <motion.div
                  variants={expertiseCardVariants}
                  initial="hidden"
                  animate={isInView ? "visible" : "hidden"}
                  whileHover="hover"
                  transition={{ delay: idx * 0.05 }}
                  style={{ width: '100%' }}
                >
                  <Paper
                    sx={{
                      p: 3,
                      background: '#0d0d0d',
                      border: '1px solid rgba(200,169,107,0.15)',
                      borderRadius: '16px',
                      width: '100%',
                      minHeight: '200px',
                      display: 'flex',
                      flexDirection: 'column',
                      textAlign: 'center',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: '#C8A96B',
                        background: '#111',
                        transform: 'translateY(-5px)',
                      },
                    }}
                  >
                    <Box sx={{ color: exp.color, fontSize: 40, mb: 2, display: 'flex', justifyContent: 'center' }}>{exp.icon}</Box>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 1.5, textAlign: 'center' }}>
                      {exp.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#aaa', lineHeight: 1.6, textAlign: 'center' }}>
                      {exp.desc}
                    </Typography>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* Global Impact Section - Centered */}
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <motion.div
                variants={itemVariants}
                style={{ width: '100%' }}
              >
                <Paper
                  sx={{
                    background: 'linear-gradient(135deg, #0d0d0d 0%, #111 100%)',
                    p: { xs: 3, md: 5 },
                    borderRadius: '24px',
                    border: '1px solid rgba(200,169,107,0.2)',
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      mb: 3,
                      color: '#C8A96B',
                      fontWeight: 700,
                      textAlign: 'center',
                    }}
                  >
                    Global Impact & Vision
                  </Typography>
                  
                  <Typography sx={{ color: '#ccc', lineHeight: 1.8, mb: 4, maxWidth: '800px', mx: 'auto', textAlign: 'center' }}>
                    With operations spanning <span style={{ color: '#C8A96B', fontWeight: 600 }}>four continents</span>, 
                    John brings unparalleled international business expertise and a network that connects 
                    markets, cultures, and opportunities across the global business landscape. His leadership 
                    has consistently delivered measurable results, including:
                  </Typography>

                  <Grid container spacing={2} sx={{ mb: 4, maxWidth: '800px', mx: 'auto', justifyContent: 'center' }}>
                    {[
                      '300% revenue growth in emerging markets',
                      'Successfully led 25+ cross-border mergers',
                      'Built teams across 15 international offices',
                      'Implemented AI-driven operational systems'
                    ].map((result, idx) => (
                      <Grid item xs={12} sm={6} key={idx} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Box sx={{ color: '#C8A96B', fontSize: 18, fontWeight: 'bold' }}>✓</Box>
                          <Typography variant="body2" sx={{ color: '#bbb', textAlign: 'left' }}>
                            {result}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  <Box
                    sx={{
                      mt: 4,
                      pt: 3,
                      borderTop: '1px solid rgba(200,169,107,0.1)',
                      maxWidth: '700px',
                      mx: 'auto',
                    }}
                  >
                    <Typography sx={{ color: '#C8A96B', fontStyle: 'italic', fontSize: '1.1rem', fontWeight: 500, textAlign: 'center' }}>
                      "Building bridges across borders, creating value everywhere. The future belongs to those who think globally and act locally."
                    </Typography>
                  </Box>
                </Paper>
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default ExecutiveBio;